import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { isAxiosError } from 'axios';
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import PublicOutlinedIcon from '@mui/icons-material/PublicOutlined';
import { DataGrid, GridColDef } from '@mui/x-data-grid';
import { apiClient } from '../../api/client';
import { TableToolbar } from '../../components/table/TableToolbar';
import { PageHeader } from '../../components/PageHeader';

interface CountryOption {
  id: number;
  name: string;
  iso2: string;
}
interface StateRow {
  id: number;
  name: string;
  countryId: number;
  isActive: boolean;
}
interface CityRow {
  id: number;
  name: string;
  stateId: number;
  isActive: boolean;
}

export const GeographyPage = () => {
  const queryClient = useQueryClient();
  const [countryId, setCountryId] = useState<number | ''>('');
  const [stateId, setStateId] = useState<number | ''>('');
  const [addStateOpen, setAddStateOpen] = useState(false);
  const [addCityOpen, setAddCityOpen] = useState(false);
  const [newName, setNewName] = useState('');
  const [error, setError] = useState<string | null>(null);

  const countriesQuery = useQuery({
    queryKey: ['lookup', '/master-data/countries'],
    queryFn: async () => (await apiClient.get('/master-data/countries')).data.data as CountryOption[],
  });

  const statesQuery = useQuery({
    queryKey: ['states', countryId],
    queryFn: async () =>
      (await apiClient.get('/master-data/states', { params: { countryId } })).data.data as StateRow[],
    enabled: countryId !== '',
  });

  const citiesQuery = useQuery({
    queryKey: ['cities', stateId],
    queryFn: async () =>
      (await apiClient.get('/master-data/cities', { params: { stateId } })).data.data as CityRow[],
    enabled: stateId !== '',
  });

  const createStateMutation = useMutation({
    mutationFn: (name: string) => apiClient.post('/master-data/states', { name, countryId }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['states', countryId] });
      setAddStateOpen(false);
      setNewName('');
      setError(null);
    },
    onError: (err) => setError(extractErrorMessage(err)),
  });

  const createCityMutation = useMutation({
    mutationFn: (name: string) => apiClient.post('/master-data/cities', { name, stateId }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['cities', stateId] });
      setAddCityOpen(false);
      setNewName('');
      setError(null);
    },
    onError: (err) => setError(extractErrorMessage(err)),
  });

  const stateColumns: GridColDef<StateRow>[] = [{ field: 'name', headerName: 'State / Province', flex: 1 }];
  const cityColumns: GridColDef<CityRow>[] = [{ field: 'name', headerName: 'City', flex: 1 }];

  return (
    <Box sx={{ p: 3 }}>
      <PageHeader
        icon={PublicOutlinedIcon}
        color="#12B76A"
        title="Geography"
        subtitle="Manage states/provinces and cities per country."
      />

      <Grid container spacing={3}>
        <Grid item xs={12} md={6}>
          <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
            <TextField
              select
              label="Country"
              size="small"
              sx={{ width: 260 }}
              value={countryId}
              onChange={(e) => {
                setCountryId(Number(e.target.value));
                setStateId('');
              }}
            >
              {countriesQuery.data?.map((c) => (
                <MenuItem key={c.id} value={c.id}>
                  {c.name}
                </MenuItem>
              ))}
            </TextField>
            <Button
              size="small"
              startIcon={<AddIcon />}
              disabled={countryId === ''}
              onClick={() => {
                setError(null);
                setAddStateOpen(true);
              }}
            >
              Add state
            </Button>
          </Stack>
          <Box sx={{ height: 420, bgcolor: 'background.paper', borderRadius: 1 }}>
            <DataGrid
              rows={statesQuery.data ?? []}
              columns={stateColumns}
              loading={statesQuery.isFetching}
              slots={{ toolbar: TableToolbar }}
              onRowClick={(params) => setStateId(params.row.id)}
              disableRowSelectionOnClick={false}
            />
          </Box>
        </Grid>

        <Grid item xs={12} md={6}>
          <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
            <Typography variant="subtitle1">
              Cities {stateId ? '' : '(select a state)'}
            </Typography>
            <Button
              size="small"
              startIcon={<AddIcon />}
              disabled={stateId === ''}
              onClick={() => {
                setError(null);
                setAddCityOpen(true);
              }}
            >
              Add city
            </Button>
          </Stack>
          <Box sx={{ height: 420, bgcolor: 'background.paper', borderRadius: 1 }}>
            <DataGrid
              rows={citiesQuery.data ?? []}
              columns={cityColumns}
              loading={citiesQuery.isFetching}
              slots={{ toolbar: TableToolbar }}
            />
          </Box>
        </Grid>
      </Grid>

      <Dialog open={addStateOpen} onClose={() => setAddStateOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>New state / province</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            {error && <Alert severity="error">{error}</Alert>}
            <TextField label="Name" autoFocus fullWidth value={newName} onChange={(e) => setNewName(e.target.value)} />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAddStateOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            disabled={!newName.trim() || createStateMutation.isPending}
            onClick={() => createStateMutation.mutate(newName.trim())}
          >
            Create
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={addCityOpen} onClose={() => setAddCityOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>New city</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            {error && <Alert severity="error">{error}</Alert>}
            <TextField label="Name" autoFocus fullWidth value={newName} onChange={(e) => setNewName(e.target.value)} />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAddCityOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            disabled={!newName.trim() || createCityMutation.isPending}
            onClick={() => createCityMutation.mutate(newName.trim())}
          >
            Create
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

const extractErrorMessage = (err: unknown): string => {
  if (isAxiosError(err)) {
    return err.response?.data?.message ?? 'Something went wrong.';
  }
  return 'Something went wrong.';
};
